import { FC, ComponentProps, useCallback } from 'react';
import { useFragment, graphql, usePaginationFragment } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';

import { HpSharedItemTilesCarouselModule } from '../HpSharedItemTilesCarouselModule/HpSharedItemTilesCarouselModule';

import {
    trackEvent,
    eventNameConstants,
    interactionTypeConstants,
    stepInteractionConstants,
    triggerConstants,
} from 'dibs-tracking';

import { flattenConnectionNodes } from 'dibs-ts-utils/exports/flattenConnectionNodes';
import { HpSharedExcellentValuesCarousel_viewer$key } from './__generated__/HpSharedExcellentValuesCarousel_viewer.graphql';
import { HpSharedExcellentValuesCarousel_user$key } from './__generated__/HpSharedExcellentValuesCarousel_user.graphql';

const viewerFragment = graphql`
    fragment HpSharedExcellentValuesCarousel_viewer on Viewer
    @refetchable(queryName: "HpSharedExcellentValuesCarouselPaginationQuery")
    @argumentDefinitions(count: { type: "Int", defaultValue: 6 }, cursor: { type: "String" }) {
        excellentValuesItems: itemSearchItems(
            first: $count
            after: $cursor
            uriRef: "/collections/excellent-values/?sort=trending"
            censored: true
            userCountryCode: $userCountryCode
            priceBookName: $priceBookName
        ) @connection(key: "HpSharedExcellentValuesCarousel_excellentValuesItems", filters: []) {
            edges {
                node {
                    ...HpSharedItemTilesCarouselModule_items
                        @arguments(
                            fetchFavorites: $hasUserId
                            userId: $userId
                            isTrade: $isTrade
                            priceBookName: $priceBookName
                        )
                }
            }
            totalResults
        }
        ...HpSharedItemTilesCarouselModule_viewer
            @arguments(fetchFavorites: $hasUserId, userId: $userId)
    }
`;

const userFragment = graphql`
    fragment HpSharedExcellentValuesCarousel_user on User {
        ...HpSharedItemTilesCarouselModule_user
    }
`;

export const HpSharedExcellentValuesCarousel: FC<{
    viewer?: HpSharedExcellentValuesCarousel_viewer$key | null;
    user?: HpSharedExcellentValuesCarousel_user$key | null;
    isMobile: boolean;
}> = ({ viewer: viewerRef, user: userRef, isMobile }) => {
    const { data: viewer, loadNext } = usePaginationFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);

    const onArrowClick: ComponentProps<typeof HpSharedItemTilesCarouselModule>['onArrowClick'] =
        useCallback(() => {
            trackEvent({
                eventName: eventNameConstants.EVENT_PRODUCT_INTERACTION,
                interaction_type: interactionTypeConstants.INTERACTION_TYPE_HOMEPAGE,
                step_interaction_name:
                    stepInteractionConstants.STEP_INTERACTION_EXCELLENT_VALUES_ARROW_CLICK,
                trigger: triggerConstants.TRIGGER_EXCELLENT_VALUES,
            });
        }, []);

    const items = flattenConnectionNodes(viewer?.excellentValuesItems);
    const totalResults = viewer?.excellentValuesItems?.totalResults || 0;

    return (
        <HpSharedItemTilesCarouselModule
            viewer={viewer}
            user={user}
            items={items}
            isMobile={isMobile}
            totalResults={totalResults}
            trackingList={triggerConstants.TRIGGER_EXCELLENT_VALUES}
            loadNext={loadNext}
            onArrowClick={onArrowClick}
            title={
                <FormattedMessage
                    id="HpSharedExcellentValuesCarousel.title"
                    defaultMessage="Excellent Values"
                />
            }
            viewMoreLink="/collections/excellent-values/?sort=newest"
            dataTn="excellent-values"
        />
    );
};
